import React, { useReducer, useImperativeHandle, forwardRef, useCallback, useMemo, useEffect } from 'react';
import { Button, Input, Badge, Card, CardBody } from 'reactstrap';
import { NERVE_DATA } from './drillData';
import { normMuscle, matchMuscle, buildMuscleAccept } from './drillUtils';

// --- Reducer ---
function initState() {
  return NERVE_DATA.flatMap(reg =>
    reg.groups.map(g => ({
      nerve: g.nerve,
      root: g.root,
      muscles: g.muscles.map(m => ({ name: m, accept: buildMuscleAccept(m) })),
      fields: g.muscles.map(() => ''),
      fieldStatuses: g.muscles.map(() => ''),
      correct: 0,
      missing: [],
      missStatus: '', // '', 'some', 'done', 'rev'
      missText: '',
    }))
  );
}

function gradeCard(card) {
  const c = { ...card };
  const used = new Array(c.muscles.length).fill(false);
  const statuses = c.fields.map(val => {
    const v = normMuscle(val);
    if (!v) return '';
    let idx = -1;
    for (let i = 0; i < c.muscles.length; i++) {
      if (!used[i] && matchMuscle(v, c.muscles[i].accept)) { idx = i; break; }
    }
    if (idx >= 0) { used[idx] = true; return 'ok'; }
    return 'no';
  });
  c.fieldStatuses = statuses;
  const missing = c.muscles.filter((m, i) => !used[i]).map(m => m.name);
  c.correct = c.muscles.length - missing.length;
  c.missing = missing;
  if (missing.length === 0) {
    c.missStatus = 'done';
    c.missText = 'All ' + c.muscles.length + ' found';
  } else {
    c.missStatus = 'some';
    c.missText = missing.join('; ');
  }
  return c;
}

function revealCard(card) {
  const c = { ...card };
  c.fields = c.muscles.map(m => m.name);
  c.fieldStatuses = c.muscles.map(() => 'rev');
  c.correct = 0;
  c.missStatus = 'rev';
  c.missText = 'Answers shown';
  c.missing = [];
  return c;
}

function reducer(state, action) {
  switch (action.type) {
    case 'SET_FIELD': {
      const s = [...state];
      const card = { ...s[action.cardIdx] };
      const fields = [...card.fields];
      const statuses = [...card.fieldStatuses];
      fields[action.fieldIdx] = action.value;
      statuses[action.fieldIdx] = '';
      card.fields = fields;
      card.fieldStatuses = statuses;
      s[action.cardIdx] = card;
      return s;
    }
    case 'GRADE_CARD': {
      const s = [...state];
      s[action.cardIdx] = gradeCard(s[action.cardIdx]);
      return s;
    }
    case 'REVEAL_CARD': {
      const s = [...state];
      s[action.cardIdx] = revealCard(s[action.cardIdx]);
      return s;
    }
    case 'CHECK_ALL':
      return state.map(gradeCard);
    case 'REVEAL_ALL':
      return state.map(revealCard);
    case 'RESET_ALL':
      return initState();
    default:
      return state;
  }
}

function inputStatusClass(status) {
  if (status === 'ok') return 'is-valid md-ok';
  if (status === 'no') return 'is-invalid md-no';
  if (status === 'rev') return 'md-rev';
  return '';
}

// --- Card Component ---
const NerveCard = React.memo(function NerveCard({ card, cardIdx, dispatch }) {
  const handleFieldChange = useCallback((fieldIdx, value) => {
    dispatch({ type: 'SET_FIELD', cardIdx, fieldIdx, value });
  }, [dispatch, cardIdx]);

  const handleFieldKey = useCallback(e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      dispatch({ type: 'GRADE_CARD', cardIdx });
    }
  }, [dispatch, cardIdx]);

  const handleCheck = useCallback(() => {
    dispatch({ type: 'GRADE_CARD', cardIdx });
  }, [dispatch, cardIdx]);

  const handleReveal = useCallback(() => {
    dispatch({ type: 'REVEAL_CARD', cardIdx });
  }, [dispatch, cardIdx]);

  let missColorClass = '';
  if (card.missStatus === 'some') missColorClass = 'text-danger';
  else if (card.missStatus === 'done') missColorClass = 'text-success font-weight-bold';
  else if (card.missStatus === 'rev') missColorClass = 'text-warning';

  return (
    <Card className="shadow-sm">
      <CardBody className="p-3">
        <div className="d-flex align-items-baseline flex-wrap mb-2" style={{ gap: '8px' }}>
          <span className="font-weight-bold" style={{ fontSize: '15px' }}>{card.nerve}</span>
          <Badge color="info" className="text-monospace" pill>{card.root}</Badge>
          <small className="text-muted text-monospace ml-auto">
            {card.muscles.length} muscle{card.muscles.length > 1 ? 's' : ''}
          </small>
        </div>
        <div className="d-flex flex-column mb-2" style={{ gap: '6px' }}>
          {card.fields.map((val, fi) => (
            <Input
              key={fi}
              bsSize="sm"
              className={inputStatusClass(card.fieldStatuses[fi])}
              type="text"
              autoComplete="off"
              spellCheck={false}
              placeholder="muscle"
              value={val}
              onChange={e => handleFieldChange(fi, e.target.value)}
              onKeyDown={handleFieldKey}
            />
          ))}
        </div>
        <div className={`mb-2 ${missColorClass}`} style={{ fontSize: '12.5px', lineHeight: 1.35, minHeight: '18px' }}>
          {card.missStatus === 'some' && (
            <><span className="font-weight-bold">Missing:</span> {card.missText}</>
          )}
          {card.missStatus === 'done' && card.missText}
          {card.missStatus === 'rev' && card.missText}
        </div>
        <div className="d-flex" style={{ gap: '7px' }}>
          <Button color="primary" size="sm" onClick={handleCheck}>Check</Button>
          <Button color="default" size="sm" outline onClick={handleReveal}>Reveal</Button>
        </div>
      </CardBody>
    </Card>
  );
});

// --- Main Component ---
const NerveMode = forwardRef(function NerveMode({ onScoreChange }, ref) {
  const [cards, dispatch] = useReducer(reducer, null, initState);

  const sections = useMemo(() => {
    let offset = 0;
    return NERVE_DATA.map(reg => {
      const start = offset;
      const count = reg.groups.length;
      offset += count;
      return { region: reg.region, start, end: offset };
    });
  }, []);

  const totalMuscles = useMemo(() => cards.reduce((a, c) => a + c.muscles.length, 0), [cards]);
  const totalNerves = cards.length;

  useEffect(() => {
    let m = 0, n = 0;
    cards.forEach(c => {
      m += c.correct;
      if (c.correct === c.muscles.length && c.correct > 0) n++;
    });
    onScoreChange({
      musclesRecalled: m,
      musclesTotal: totalMuscles,
      nervesComplete: n,
      nervesTotal: totalNerves,
    });
  }, [cards, totalMuscles, totalNerves, onScoreChange]);

  useImperativeHandle(ref, () => ({
    checkAll: () => dispatch({ type: 'CHECK_ALL' }),
    revealAll: () => dispatch({ type: 'REVEAL_ALL' }),
    resetAll: () => dispatch({ type: 'RESET_ALL' }),
  }), []);

  return (
    <div>
      {sections.map((sec, si) => (
        <section key={si} className="mb-4">
          <div className="md-region-head">
            <h3 className="mb-0 h5 font-weight-bold">{sec.region}</h3>
          </div>
          <div className="md-grid">
            {cards.slice(sec.start, sec.end).map((card, ci) => (
              <NerveCard
                key={ci}
                card={card}
                cardIdx={sec.start + ci}
                dispatch={dispatch}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
});

export default NerveMode;
